/**
 * MultiPayerCalculationService
 *
 * Business logic for multi-payer calculations and distribution.
 * Extracted from MultiPayerSelect component while preserving exact behavior.
 */
import { roundToCurrencyPrecision } from '@splitfiesta/shared';

export interface Payer {
  userId: string;
  amount: number;
}

export class MultiPayerCalculationService {
  /**
   * Calculate total amount from payers
   * Always sums the actual payer amounts to accurately reflect allocation status
   */
  static calculateTotal(
    payers: Payer[],
    _totalAmount: number,
    _currency: string
  ): number {
    // Always sum the actual payer amounts to accurately show allocation status
    return payers.reduce((sum, p) => sum + (p.amount || 0), 0);
  }

  /**
   * Calculate remaining amount to allocate
   */
  static calculateRemaining(totalAmount: number, currentTotal: number): number {
    return totalAmount - currentTotal;
  }

  /**
   * Auto-set amount for single payer when they're selected
   */
  static autoSetSinglePayerAmount(
    userId: string,
    totalAmount: number,
    currency: string
  ): number | null {
    if (userId && totalAmount > 0) {
      return roundToCurrencyPrecision(totalAmount, currency || 'USD');
    }
    return null;
  }

  /**
   * Distribute amount evenly among all members
   */
  static distributeAmongAllMembers(
    members: Array<{ userId: string | { _id: string } }>,
    totalAmount: number,
    currency: string
  ): Payer[] {
    if (!members || members.length === 0) return [];

    const amountPerPayer =
      totalAmount > 0
        ? roundToCurrencyPrecision(totalAmount / members.length, currency)
        : 0;

    return members
      .map((member, index) => {
        if (!member || !member.userId) {
          return null;
        }

        // Handle both formats
        const memberId =
          typeof member.userId === 'string' ? member.userId : member.userId._id;

        if (totalAmount <= 0) {
          return { userId: memberId, amount: 0 };
        }

        const isLast = index === members.length - 1;
        const amount = isLast
          ? roundToCurrencyPrecision(
              totalAmount - amountPerPayer * (members.length - 1),
              currency
            )
          : amountPerPayer;

        return { userId: memberId, amount };
      })
      .filter((p): p is Payer => p !== null && p.userId !== undefined);
  }

  /**
   * Split amount evenly among valid payers
   */
  static splitEvenly(
    payers: Payer[],
    totalAmount: number,
    currency: string
  ): Payer[] {
    if (payers.length === 0) return payers;

    const validPayers = payers.filter(p => p.userId);
    if (validPayers.length === 0 || totalAmount <= 0) return payers;

    const amountPerPayer = roundToCurrencyPrecision(
      totalAmount / validPayers.length,
      currency
    );

    return payers.map(payer => {
      if (!payer.userId) return payer;

      const validIndex = validPayers.findIndex(p => p.userId === payer.userId);
      const isLast = validIndex === validPayers.length - 1;

      const amount = isLast
        ? roundToCurrencyPrecision(
            totalAmount - amountPerPayer * (validPayers.length - 1),
            currency
          )
        : amountPerPayer;

      return { ...payer, amount };
    });
  }

  /**
   * Auto-complete remaining amount for empty payers
   */
  static autoCompleteRemaining(
    payers: Payer[],
    remaining: number,
    currency: string
  ): Payer[] {
    // Find payers with userId but no amount (0)
    const emptyPayers = payers.filter(p => p.userId && (p.amount || 0) === 0);
    if (emptyPayers.length === 0 || remaining <= 0) return payers;

    // Calculate amount per empty payer
    const amountPerEmptyPayer = roundToCurrencyPrecision(
      remaining / emptyPayers.length,
      currency
    );

    // Track how much we've allocated to handle rounding
    let allocatedAmount = 0;
    let emptyPayerIndex = 0;

    return payers.map(payer => {
      if (!payer.userId || (payer.amount || 0) > 0) {
        // Keep existing payers unchanged
        return payer;
      }

      // This is an empty payer that needs auto-complete
      const isLastEmptyPayer = emptyPayerIndex === emptyPayers.length - 1;
      let amount;

      if (isLastEmptyPayer) {
        // Last empty payer gets the exact remaining amount
        amount = roundToCurrencyPrecision(
          remaining - allocatedAmount,
          currency
        );
      } else {
        amount = amountPerEmptyPayer;
        allocatedAmount += amount;
      }

      emptyPayerIndex++;
      return { ...payer, amount };
    });
  }

  /**
   * Get display amount for single payer (handles real-time sync)
   */
  static getSinglePayerDisplayAmount(
    payer: Payer,
    _payersLength: number,
    _totalAmount: number,
    currency: string
  ): string {
    // Always use payer.amount for display - the auto-sync with totalAmount
    // happens via useEffect, not here. This allows user edits to be reflected.
    return payer.amount
      ? roundToCurrencyPrecision(payer.amount, currency || 'USD').toString()
      : '';
  }

  /**
   * Check if member is already selected as payer
   */
  static isMemberAlreadySelected(
    memberId: string,
    payers: Payer[],
    currentIndex: number
  ): boolean {
    return payers.some(
      (p, pIndex) => pIndex !== currentIndex && p.userId === memberId
    );
  }

  /**
   * Update single payer amount when total changes
   */
  static shouldUpdateSinglePayerAmount(
    payers: Payer[],
    totalAmount: number,
    lastProcessedAmount: number,
    isUpdatingFromValue: boolean
  ): boolean {
    // Skip if we're updating from external value to prevent conflicts
    if (isUpdatingFromValue) return false;

    // Skip if totalAmount hasn't actually changed
    if (totalAmount === lastProcessedAmount) return false;

    // Only proceed if we have a single payer with userId and valid amount
    return payers.length === 1 && !!payers[0].userId && totalAmount > 0;
  }

  /**
   * Check if amount has significantly changed (to prevent unnecessary updates)
   */
  static hasAmountChanged(amount1: number, amount2: number): boolean {
    return Math.abs(amount1 - amount2) > 0.001;
  }
}

export const multiPayerCalculationService = new MultiPayerCalculationService();
